import { useEffect, useRef, useState } from "react";
import { useOpenTurnstileMutation } from "../../api";
import { UnmannedExitInstructions } from "./unmanned-exit-instructions";
import { FaceGateError, useFaceGate } from "./useFaceGate";
import { Button, Dialog, Flex, Heading, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import Countdown from "react-countdown";
import "./unmanned-exit.css";
import { ERROR_MODAL_AUTO_CLOSE_TIMEOUT } from "../../shared/consts";
import { useKioskBusy } from "../../shared/lib/kiosk-activity";
import { errorSound, turnstileOpenSound } from "./sounds";
import type {
  KioskCameraDegradedContext,
  KioskPermissionDeniedContext,
} from "./types";
import { normalizeRequestFailure } from "./request-failure";
import { usePostHog } from "@posthog/react";
import {
  isInfrastructureFailure,
  type OperationCircuitController,
} from "./use-operation-circuit";
import {
  FaceCaptureRelayError,
  submitFaceCapture,
} from "../../api/face-capture-relay";

const TURNSTILE_TIMEOUT_MS = 15_000;

type ExitDialog =
  | {
      kind: "turnstile_failed";
      reason: "timeout" | "permission" | "unavailable";
    }
  | { kind: "success" }
  | null;

type FaceGateOutcome = "passed" | "detection_rejected" | "technical_error";

type FaceGateDecision = {
  outcome: FaceGateOutcome;
  stage?: string;
  reason?: string;
  ratio?: number;
};

type UnmannedExitProps = {
  storeKey: string;
  zoneId: string;
  onPermissionDenied: (context: KioskPermissionDeniedContext) => void;
  operationCircuit: OperationCircuitController;
  onCameraDegraded?: (context: KioskCameraDegradedContext) => void;
  onCameraRecovered?: () => void;
};

export function UnmannedExit({
  storeKey,
  zoneId,
  onPermissionDenied,
  operationCircuit,
  onCameraDegraded,
  onCameraRecovered,
}: UnmannedExitProps) {
  const [openTurnstileMutation] = useOpenTurnstileMutation();
  const posthog = usePostHog();
  const { videoRef, status, errorMessage, detectOnce } = useFaceGate();
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<ExitDialog>(null);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const turnstileRequestRef = useRef<{ abort: () => void } | null>(null);
  const {
    canAttempt: canAttemptTurnstile,
    recordFailure: recordTurnstileFailure,
    recordSuccess: recordTurnstileSuccess,
  } = operationCircuit;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      turnstileRequestRef.current?.abort();
      turnstileRequestRef.current = null;
    };
  }, []);

  // Busy while detection/turnstile is in flight or the result dialog is shown.
  useKioskBusy(busy || dialog !== null);

  const eventContext = (attemptId: string) => ({
    attempt_id: attemptId,
    zone_id: zoneId,
    online: navigator.onLine,
    visibility_state: document.visibilityState,
  });

  async function openTurnstile(attemptId: string, faceGate: FaceGateDecision) {
    const bypass = faceGate.outcome !== "passed";
    const faceGateProperties = {
      face_gate_outcome: faceGate.outcome,
      face_gate_stage: faceGate.stage,
      face_gate_reason: faceGate.reason,
      face_width_ratio: faceGate.ratio,
    };
    const startedAt = performance.now();
    posthog.capture("turnstile_open_started", {
      ...eventContext(attemptId),
      bypass,
      ...faceGateProperties,
    });
    const request = openTurnstileMutation({ zoneId });
    turnstileRequestRef.current = request;
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      request.abort();
    }, TURNSTILE_TIMEOUT_MS);

    try {
      await request.unwrap();
      if (!mountedRef.current) return;
      recordTurnstileSuccess();
      setDialog({ kind: "success" });
      turnstileOpenSound.play();
      posthog.capture("turnstile_opened", {
        ...eventContext(attemptId),
        bypass,
        ...faceGateProperties,
        duration_ms: Math.round(performance.now() - startedAt),
      });
    } catch (error) {
      if (!mountedRef.current) return;
      const failure = normalizeRequestFailure(error);
      const isTimeout = timedOut || failure.kind === "timeout";
      const eventName = isTimeout
        ? "turnstile_open_timeout"
        : "turnstile_open_failed";
      posthog.capture(eventName, {
        ...eventContext(attemptId),
        bypass,
        ...faceGateProperties,
        duration_ms: Math.round(performance.now() - startedAt),
        timeout_ms: isTimeout ? TURNSTILE_TIMEOUT_MS : undefined,
        failure_kind: isTimeout ? "timeout" : failure.kind,
        http_status: failure.status,
        error_name: failure.errorName,
        error_code: failure.errorCode,
      });
      if (failure.kind === "permission") {
        setDialog({ kind: "turnstile_failed", reason: "permission" });
        onPermissionDenied({ operation: "turnstile_open", status: 403 });
      } else {
        if (isInfrastructureFailure(failure.status)) recordTurnstileFailure();
        setDialog({
          kind: "turnstile_failed",
          reason: isTimeout ? "timeout" : "unavailable",
        });
      }
      errorSound.play();
    } finally {
      window.clearTimeout(timeoutId);
      turnstileRequestRef.current = null;
    }
  }

  async function runFaceGate(attemptId: string) {
    posthog.capture("face_gate_started", {
      ...eventContext(attemptId),
      gate_status: status,
      policy: "observe_only",
    });

    if (status !== "ready" && status !== "detecting") {
      const unavailable = new FaceGateError(
        errorMessage ?? "Face detector is not ready.",
        "model",
        "model_unavailable",
        false,
        0,
      );
      posthog.capture("face_gate_finished", {
        ...eventContext(attemptId),
        outcome: "technical_error",
        stage: unavailable.stage,
        reason: unavailable.reason,
        retryable: unavailable.retryable,
        retry_count: 0,
        duration_ms: 0,
      });
      await bypassTechnicalFaceGate(attemptId, unavailable);
      return;
    }

    const startedAt = performance.now();
    try {
      const result = await detectOnce();
      void submitFaceCapture({
        eventId: attemptId,
        storeKey,
        zoneId,
        result,
      }).catch((error: unknown) => {
        posthog.capture("face_capture_relay_failed", {
          ...eventContext(attemptId),
          http_status:
            error instanceof FaceCaptureRelayError ? error.status : undefined,
          stage:
            error instanceof FaceCaptureRelayError ? error.stage : undefined,
          error_name: error instanceof Error ? error.name : "UnknownRelayError",
        });
      });
      posthog.capture("face_gate_finished", {
        ...eventContext(attemptId),
        outcome: result.passed ? "passed" : "rejected",
        reason: result.passed ? undefined : result.reason,
        retry_count: 0,
        duration_ms: Math.round(performance.now() - startedAt),
        face_width_ratio: result.ratio,
        face_count: result.faceCount,
        frame_width: result.frameWidth,
        frame_height: result.frameHeight,
        frame_brightness: result.brightness,
      });
      onCameraRecovered?.();

      if (result.passed) {
        await openTurnstile(attemptId, {
          outcome: "passed",
          ratio: result.ratio,
        });
      } else {
        posthog.capture("face_gate_bypassed", {
          ...eventContext(attemptId),
          bypass_kind: "detection_rejected",
          policy: "observe_only",
          reason: result.reason,
          retry_count: 0,
          face_width_ratio: result.ratio,
          face_count: result.faceCount,
          frame_width: result.frameWidth,
          frame_height: result.frameHeight,
          frame_brightness: result.brightness,
        });
        await openTurnstile(attemptId, {
          outcome: "detection_rejected",
          reason: result.reason,
          ratio: result.ratio,
        });
      }
    } catch (error) {
      if (!mountedRef.current) return;
      const technicalError = normalizeFaceGateError(error, startedAt);
      if (!(error instanceof FaceGateError)) {
        posthog.captureException(error, {
          ...eventContext(attemptId),
          failure_stage: technicalError.stage,
        });
      }
      posthog.capture("face_gate_finished", {
        ...eventContext(attemptId),
        outcome: "technical_error",
        stage: technicalError.stage,
        reason: technicalError.reason,
        retryable: technicalError.retryable,
        retry_count: 0,
        duration_ms: Math.round(technicalError.durationMs),
        video_ready_state: technicalError.diagnostics.readyState,
        video_width: technicalError.diagnostics.videoWidth,
        video_height: technicalError.diagnostics.videoHeight,
      });
      await bypassTechnicalFaceGate(attemptId, technicalError);
    }
  }

  async function bypassTechnicalFaceGate(
    attemptId: string,
    error: FaceGateError,
  ) {
    if (!mountedRef.current) return;
    const properties = {
      ...eventContext(attemptId),
      stage: error.stage,
      reason: error.reason,
      retry_count: 0,
      bypass_kind: "technical_error",
      policy: "observe_only",
    };
    posthog.capture("face_gate_bypassed", properties);
    // One-release migration event for dashboards that still count the old,
    // misattributed signal. New dashboards must exclude legacy_misattributed.
    posthog.capture("turnstile_open_failed", {
      ...properties,
      failure_stage: "face_gate",
      legacy_misattributed: true,
    });
    onCameraDegraded?.({
      operation: "face_gate",
      stage: error.stage,
      reason: error.reason,
      retryCount: 0,
      staffActionRequired: error.reason === "permission_denied",
    });
    await openTurnstile(attemptId, {
      outcome: "technical_error",
      stage: error.stage,
      reason: error.reason,
    });
  }

  const handleOpenTurnstile = () => {
    if (inFlightRef.current || !canAttemptTurnstile) return;
    inFlightRef.current = true;
    setBusy(true);
    const attemptId = createAttemptId();
    void runFaceGate(attemptId).finally(() => {
      inFlightRef.current = false;
      if (mountedRef.current) setBusy(false);
    });
  };

  const onClose = () => {
    setDialog(null);
    setBusy(false);
  };

  return (
    <div className="bg-white h-screen mobile-fa">
      <UnmannedExitInstructions
        storeKey={storeKey}
        onCheckout={handleOpenTurnstile}
        loading={busy || status === "idle" || status === "initializing"}
      />

      <Dialog.Root open={dialog !== null}>
        <Dialog.Content
          className="!rounded-[32px] min-h-[500px] mobile-fa"
          minWidth="540px"
        >
          {dialog?.kind === "turnstile_failed" ? (
            <TurnstileFailed
              reason={dialog.reason}
              loading={busy}
              onClose={onClose}
              onRetry={handleOpenTurnstile}
            />
          ) : (
            <TurnstileOpen
              setTurnstileOpen={(open) =>
                setDialog(open ? { kind: "success" } : null)
              }
            />
          )}
        </Dialog.Content>
      </Dialog.Root>

      <video ref={videoRef} className="hidden-video" muted playsInline />
    </div>
  );
}

function TurnstileFailed({
  reason,
  loading,
  onClose,
  onRetry,
}: {
  reason: "timeout" | "permission" | "unavailable";
  loading: boolean;
  onClose: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const descriptionKey =
    reason === "timeout"
      ? "turnstileTimeoutDesc"
      : reason === "permission"
        ? "turnstilePermissionDesc"
        : "turnstileErrorDesc";

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      className="!h-full"
      p="4"
    >
      <video
        height={400}
        className="h-[400px] mt-2 pointer-events-none select-none"
        src="/unmanned/green-ray-animation-out.mp4"
        autoPlay
        loop
        playsInline
        muted
      />
      <Heading
        size="6"
        mb="2"
        className="!text-3xl"
        align="center"
        weight="bold"
        color="gray"
        highContrast
      >
        {t("turnstileErrorTitle")}
      </Heading>
      <Flex direction="column" gap="3" mb="6">
        <Text as="p" size="5" align="center" color="gray">
          {t(descriptionKey)}
        </Text>
      </Flex>
      <Flex className="w-full" gap="4">
        {reason !== "permission" ? (
          <Button
            size="4"
            className="!basis-[calc(50%-0.5rem)] !min-h-14 !rounded-2xl"
            variant="solid"
            highContrast
            radius="large"
            color="gray"
            onClick={onRetry}
            loading={loading}
          >
            {t("tryAgain")}
          </Button>
        ) : null}
        <Button
          size="4"
          className="!basis-[calc(50%-0.5rem)] !min-h-14 !rounded-2xl"
          variant="outline"
          highContrast
          disabled={loading}
          radius="large"
          color="gray"
          onClick={onClose}
        >
          {t("close")} {!loading && <Timeout onClose={onClose} />}
        </Button>
      </Flex>
    </Flex>
  );
}

function Timeout({ onClose }: { onClose: () => void }) {
  const [closeDate] = useState(
    () => Date.now() + ERROR_MODAL_AUTO_CLOSE_TIMEOUT,
  );

  return (
    <Countdown
      date={closeDate}
      onComplete={onClose}
      renderer={({ seconds }) => <>({seconds} s)</>}
    />
  );
}

function TurnstileOpen({
  setTurnstileOpen,
}: {
  setTurnstileOpen: (value: boolean) => void;
}) {
  const [closeDate] = useState(() => Date.now() + 6000);
  const { t } = useTranslation();

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      className="!h-full"
    >
      <video
        height={400}
        className="h-[400px] pointer-events-none select-none"
        src="/unmanned/turnstile-open-animation-out-2.mp4"
        autoPlay
        playsInline
        muted
      />

      <Heading
        size="6"
        mb="2"
        className="!text-3xl"
        align="center"
        weight="bold"
        color="gray"
        highContrast
      >
        {t("turnstileSuccess")}
      </Heading>
      <Flex direction="column" gap="3" mb="6">
        <Text as="p" size="5" align="center" color="gray">
          <Countdown
            date={closeDate}
            onComplete={() => setTurnstileOpen(false)}
            renderer={({ seconds }) => (
              <>{t("turnstileSuccessHint", { nSeconds: seconds })}</>
            )}
          ></Countdown>
        </Text>
      </Flex>
    </Flex>
  );
}

function normalizeFaceGateError(
  error: unknown,
  startedAt: number,
): FaceGateError {
  if (error instanceof FaceGateError) return error;
  return new FaceGateError(
    "Unexpected face detection failure.",
    "inference",
    "unexpected",
    true,
    performance.now() - startedAt,
  );
}

function createAttemptId(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
